import React, { useCallback, useMemo, memo } from 'react';
import { propOr } from 'ramda';
import classNames from 'classnames';
import {
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  TextField,
  FormControl,
  FormHelperText,
  Link,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { SanitizedHTML } from '@alycecom/ui';
import { useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import { useExternalErrors } from '@alycecom/hooks';
import { TLegacyErrors } from '@alycecom/services';

import {
  EMAIL_RA_TYPE,
  PHONE_RA_TYPE,
  QUESTION_RA_TYPE,
  TERMS_RA_TYPE,
} from '../../../constants/requiredActions.constants';
import { getActionTypes, getActionTypeValueFn } from '../../../store/requiredActions/requiredActions.helpers';
import StyledSubmitButton from '../../../../../components/Shared/StyledSubmitButton';
import { getProspects } from '../../../store/giftFlow/giftFlow.selectors';
import { buildValidationSchema } from '../../../store/requiredActions/requiredActions.schemas';
import { IRequiredAction, IRequiredActionsValidateParams } from '../../../store/requiredActions/requiredActions.types';
import { BrandingFields } from '../../../../../types/Branding.types';
import { getBranding } from '../../../../../store/branding/branding.selectors';

const useStyles = makeStyles(({ palette }) => ({
  question: {
    color: palette.primary.main,
  },
  termsTitle: {
    color: palette.grey.main,
    lineHeight: '16px !important',
  },
  terms: {
    color: palette.grey.main,
  },
  termsLabel: {
    height: 24,
  },
}));

export interface IRequiredActionsFormProps {
  isLoading?: boolean;
  errors?: TLegacyErrors;
  onSubmit: (params: IRequiredActionsValidateParams) => void;
  requiredActions?: IRequiredAction[];
}
const RequiredActionsForm = ({
  isLoading = false,
  errors: externalErrors = {},
  onSubmit,
  requiredActions = [],
}: IRequiredActionsFormProps) => {
  const classes = useStyles();

  const prospects = useSelector(getProspects);
  const customStyling = useSelector(getBranding);

  const { useEmail, usePhone, useQuestion, usePolicy, questionsValue, termsValue } = useMemo(() => {
    const actionTypes = getActionTypes(requiredActions);
    const termsText = getActionTypeValueFn(TERMS_RA_TYPE)(requiredActions) as string;
    const questionsText = getActionTypeValueFn(QUESTION_RA_TYPE)(requiredActions) as string;
    const termsValueText = termsText.replace(/(?:\r\n|\r|\n)/g, '<br />');
    const questionsValueText = questionsText.replace(/(?:\r\n|\r|\n)/g, '<br />');
    return {
      useEmail: actionTypes.includes(EMAIL_RA_TYPE),
      usePhone: actionTypes.includes(PHONE_RA_TYPE),
      useQuestion: actionTypes.includes(QUESTION_RA_TYPE),
      usePolicy: actionTypes.includes(TERMS_RA_TYPE),
      questionsValue: questionsValueText,
      termsValue: termsValueText,
    };
  }, [requiredActions]);

  const { errors, handleSubmit, control, setError } = useForm({
    mode: 'all',
    defaultValues: {
      email: propOr('', 'email', prospects),
      phone: propOr('', 'phone', prospects),
      answer: propOr('', 'answer', prospects),
      policy: propOr(false, 'policy', prospects),
    },
    resolver: yupResolver(buildValidationSchema({ useEmail, usePhone, useQuestion, usePolicy })),
  });

  const handleSubmitForm = useCallback(
    data => {
      onSubmit(data);
    },
    [onSubmit],
  );

  useExternalErrors(setError, externalErrors);

  return (
    <form onSubmit={handleSubmit(handleSubmitForm)}>
      {useEmail && (
        <Box mt={2}>
          <Controller
            control={control}
            name="email"
            render={({ onChange, value }) => (
              <TextField
                id="field-email-id"
                name="email"
                label="Email"
                placeholder="Email"
                fullWidth
                variant="outlined"
                disabled={isLoading}
                value={value}
                error={!!errors.email}
                helperText={errors.email && errors.email.message}
                onChange={onChange}
              />
            )}
          />
        </Box>
      )}

      {usePhone && (
        <Box mt={2}>
          <Controller
            control={control}
            name="phone"
            render={({ onChange, value }) => (
              <TextField
                id="field-phone-id"
                name="phone"
                label="Phone Number"
                placeholder="Phone Number"
                fullWidth
                data-private="lipsum"
                variant="outlined"
                disabled={isLoading}
                value={value}
                error={!!errors.phone}
                helperText={errors.phone && errors.phone.message}
                onChange={onChange}
              />
            )}
          />
        </Box>
      )}

      {useQuestion && (
        <Box mt={2} textAlign="left">
          <Typography className={classNames('Body-Regular-Left-Chambray-Bold', classes.question)}>
            Questions to answer
          </Typography>
          <Box
            data-testid="questionActionValue"
            className={classNames('Body-Regular-Left-Chambray', classes.question)}
            mb={2}
          >
            <SanitizedHTML html={questionsValue} />
          </Box>
          <Controller
            control={control}
            name="answer"
            render={({ onChange, value }) => (
              <TextField
                id="field-answer-id"
                name="answer"
                multiline
                fullWidth
                variant="outlined"
                rows={8}
                disabled={isLoading}
                value={value}
                error={!!errors.answer}
                helperText={errors.answer && errors.answer.message}
                inputProps={{ 'data-testid': 'RequiredActions.AnswerField' }}
                onChange={onChange}
              />
            )}
          />
        </Box>
      )}

      {usePolicy && (
        <>
          <Box mt={2} textAlign="left">
            <Typography
              data-testid="termsValueHeader"
              className={classNames('Label-Table-Left-Static', classes.termsTitle)}
            >
              Terms to agree
            </Typography>
            <Box data-testid="termsValueText">
              <SanitizedHTML className={classNames('Body-Small-Inactive', classes.terms)} html={termsValue} />
            </Box>
          </Box>
          <Box textAlign="left" mt={1}>
            <Controller
              control={control}
              name="policy"
              render={({ onChange, value }) => (
                <FormControl error={!!errors.policy}>
                  <FormControlLabel
                    data-testid="RequiredActions.FormControlLabel.Policy"
                    disabled={isLoading}
                    control={<Checkbox checked={value} onChange={e => onChange(e.target.checked)} color="primary" />}
                    label={
                      <Typography
                        data-testid="submitTermsCheckbox"
                        className={classNames('Body-Regular-Left-Inactive', classes.terms)}
                      >
                        I agree to these terms
                      </Typography>
                    }
                  />
                  {errors.policy && <FormHelperText>{errors.policy.message}</FormHelperText>}
                </FormControl>
              )}
            />
          </Box>
        </>
      )}

      <Box mt={2} width={1}>
        <StyledSubmitButton type="submit" data-testid="submitRequiredActionButton" width="100%" disabled={isLoading}>
          Continue
        </StyledSubmitButton>
      </Box>
      <Box mt={2} color="#9ea9c6" fontFamily="Lato" textAlign="center">
        {customStyling[BrandingFields.companyName]} may share some of your information with the gift sender, but will
        not share your mailing address. To learn more, please see our{' '}
        <Link
          style={{ display: 'inline' }}
          href={customStyling[BrandingFields.privacyPolicyLink] || 'https://www.alyce.com/privacy-policy'}
          target="_blank"
          rel="noopener noreferrer"
        >
          Privacy Policy
        </Link>
      </Box>
    </form>
  );
};

export default memo(RequiredActionsForm);
